import React, { useState } from "react";
import {
  EXTENSION_GROUPS,
  normalizeAllowedExtensions,
  DEFAULT_ALLOWED_EXTENSIONS,
  DISABLED_EXTENSIONS,
} from "../utils/uploadLimits";
import { showErrorToast } from "../utils/toastUtils";

/**
 * Enterprise Settings control for the allowed-upload-extensions allow-list.
 * Renders one checkbox per known extension (grouped by category) plus an
 * input box to add custom extensions. The value is stored as a
 * comma-separated string (backend format) or an array — both are handled.
 *
 * Extensions in DISABLED_EXTENSIONS are shown greyed-out and cannot be
 * toggled — they are "in development" and get enabled from code later by
 * removing them from that list.
 *
 * When no config exists yet (null/empty), the DEFAULT_ALLOWED_EXTENSIONS are
 * pre-checked so the UI reflects the effective behavior (defaults are active
 * until the admin explicitly saves a list). This avoids the footgun where
 * checking a single box on a blank list would wipe out every other type.
 */
const AllowedExtensionsEditor = ({ value, onChange }) => {
  const [customExt, setCustomExt] = useState("");

  const configured = normalizeAllowedExtensions(value);
  // Effective selection: enterprise list when configured, otherwise defaults.
  // Disabled (in-development) extensions are always dropped — never selectable
  // and never persisted, even if a stale config from before they were disabled
  // still contains them.
  const selected = new Set(
    (configured.length ? configured : DEFAULT_ALLOWED_EXTENSIONS).filter(
      (e) => !DISABLED_EXTENSIONS.includes(e),
    ),
  );
  const isConfigured = configured.length > 0;

  const knownExts = new Set(
    EXTENSION_GROUPS.flatMap((g) => [...g.exts, ...(g.disabledExts || [])]),
  );
  // Extensions that are currently selected but not part of any known group
  // (i.e. added through the input box).
  const customExts = [...selected].filter((e) => !knownExts.has(e));

  const emit = (nextSet) => {
    onChange([...nextSet].join(","));
  };

  const toggle = (ext) => {
    const next = new Set(selected);
    if (next.has(ext)) next.delete(ext);
    else next.add(ext);
    emit(next);
  };

  const addCustom = () => {
    const ext = customExt.trim().toLowerCase().replace(/^\./, "");
    if (!ext) return;
    if (!/^[a-z0-9]{1,10}$/.test(ext)) {
      setCustomExt("");
      return;
    }
    if (DISABLED_EXTENSIONS.includes(ext)) {
      setCustomExt("");
      showErrorToast(`".${ext}" is in development and cannot be enabled yet.`);
      setCustomExt("");
      return;
    }
    const next = new Set(selected);
    next.add(ext);
    emit(next);
    setCustomExt("");
  };

  return (
    <div className="allowed-extensions-editor">
      <label className="d-block">
        Allowed File Extensions
        <small className="text-muted d-block">
          Check the file types users are allowed to upload. Unchecked extensions
          are rejected on all upload surfaces (chat, documents, training &amp; My
          Drive). If every box is unchecked, the default list is used.{" "}
          {!isConfigured && (
            <span className="fw-semibold">
              Not configured yet — the default list below is active. Save to
              apply your custom selection.
            </span>
          )}
          <span className="d-block mt-1">
            <span className="text-secondary opacity-50">Greyed-out</span>{" "}
            extensions are in development and cannot be enabled yet.
          </span>
        </small>
      </label>

      {EXTENSION_GROUPS.map((group) => (
        <div key={group.label} className="mb-2">
          <div className="fw-semibold small text-muted mb-1">
            {group.label}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {group.exts.map((ext) => (
              <label
                key={ext}
                className="form-check form-check-inline align-items-center m-0"
                style={{ cursor: "pointer" }}
              >
                <input
                  className="form-check-input me-1"
                  type="checkbox"
                  checked={selected.has(ext)}
                  onChange={() => toggle(ext)}
                />
                <span className="form-check-label small">
                  .{ext}
                </span>
              </label>
            ))}
            {(group.disabledExts || []).map((ext) => (
              <label
                key={ext}
                className="form-check form-check-inline align-items-center m-0 opacity-50"
                title={`${ext} — in development, will be enabled later`}
                style={{ cursor: "not-allowed" }}
              >
                <input
                  className="form-check-input me-1"
                  type="checkbox"
                  disabled
                />
                <span className="form-check-label small text-decoration-line-through">
                  .{ext}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}

      {customExts.length > 0 && (
        <div className="mb-2">
          <div className="fw-semibold small text-muted mb-1">Custom</div>
          <div className="d-flex flex-wrap gap-2">
            {customExts.map((ext) => (
              <span
                key={ext}
                className="badge rounded-pill text-bg-primary d-inline-flex align-items-center gap-1"
              >
                .{ext}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  style={{ fontSize: "0.5rem" }}
                  aria-label={`Remove .${ext}`}
                  onClick={() => {
                    const next = new Set(selected);
                    next.delete(ext);
                    emit(next);
                  }}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* <div className="d-flex gap-2 align-items-center mt-2">
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: "180px" }}
          placeholder="e.g. csv"
          value={customExt}
          onChange={(e) => setCustomExt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={addCustom}
        >
          + Add Extension
        </button>
      </div> */}
    </div>
  );
};

export default AllowedExtensionsEditor;
